import { useMemo } from 'react';
import {
  useTracks,
  type TrackReference,
  type TrackReferenceOrPlaceholder
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { orderTiles } from '../components/video/tile-order.js';

interface CallTiles {
  /**
   * One reference per tile, in the stable order: the local participant, then
   * everyone else by join time ({@link orderTiles}). Speaking and camera state
   * play no part, or the tiles would reshuffle whenever someone talks (#104).
   */
  tiles: TrackReferenceOrPlaceholder[];

  /** Subscribed microphone per participant identity, for the speaking ring */
  microphoneTrackRefs: ReadonlyMap<string, TrackReference>;
}

/**
 * The tiles of the call in the current `RoomContext`: every surface that
 * shows the call (the corner tiles, full screen) reads them from here, so
 * they agree on what a tile is and in which order they come.
 *
 * `withPlaceholder: true` gives a camera reference for *every* participant,
 * including the local one, so a participant with the camera off still has a
 * tile with their name and avatar.
 */
export function useCallTiles(): CallTiles {
  // One subscription for everything the tiles need. Microphones ride along so
  // each tile can measure its own speech level (#103); they never become
  // tiles of their own.
  const allTracks = useTracks([
    { source: Track.Source.Camera, withPlaceholder: true },
    { source: Track.Source.ScreenShare, withPlaceholder: false },
    { source: Track.Source.Microphone, withPlaceholder: false }
  ], { onlySubscribed: false });

  const { visual, microphoneTrackRefs } = useMemo(() => {
    const tracks: TrackReferenceOrPlaceholder[] = [];
    const microphones = new Map<string, TrackReference>();
    allTracks.forEach((track) => {
      if (track.source !== Track.Source.Microphone) {
        tracks.push(track);
      } else if (track.publication?.track) {
        // A remote microphone has a track only while subscribed; without
        // one there is nothing to measure, which reads as "not speaking".
        microphones.set(track.participant.identity, track as TrackReference);
      }
    });
    return { visual: tracks, microphoneTrackRefs: microphones };
  }, [allTracks]);

  // Local first, then join order; never by who is speaking (#104).
  const tiles = useMemo(() => orderTiles(visual), [visual]);

  return { tiles, microphoneTrackRefs };
}
